import React, { useEffect, useRef } from 'react';
import { Check } from 'lucide-react';
import type { SuccessModalProps } from '../types/createGroup';

const SuccessModal: React.FC<SuccessModalProps> = ({
  isOpen,
  groupName,
  onComplete,
  autoRedirectSeconds = 2,
  hasPermissions = true
}) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const onCompleteRef = useRef(onComplete);
  
  // Update the ref when onComplete changes, but don't trigger effect
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!isOpen) {
      // Clear any existing timer when modal closes
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    console.log('SuccessModal: Setting timer for', autoRedirectSeconds, 'seconds');
    
    // Clear any existing timer before setting new one
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    timerRef.current = setTimeout(() => {
      console.log('SuccessModal: Timer expired, calling onComplete');
      if (onCompleteRef.current) {
        onCompleteRef.current();
      }
    }, autoRedirectSeconds * 1000);

    return () => {
      console.log('SuccessModal: Cleaning up timer');
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isOpen, autoRedirectSeconds]); // 🔥 FIX: Remove onComplete from dependencies

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="success-modal-backdrop">
      <div className="success-modal-container">
        <div className="success-modal-body">
          <div className="success-modal-content">
            <div className="success-modal-icon-container">
              <div className="success-modal-icon">
                <Check size={32} />
              </div>
            </div>
            
            <div className="success-modal-title">
              <h2>Success!</h2>
            </div>
            
            <div className="success-modal-message">
              <p>
                Group "{groupName}" has been created successfully
                {hasPermissions ? ' with permissions' : ''}
              </p>
            </div>
            
            <div className="success-modal-redirect">
              <div className="success-modal-progress">
                <div
                  className="success-modal-progress-bar"
                  style={{
                    animation: `successProgress ${autoRedirectSeconds}s linear forwards`
                  }}
                />
              </div>
              <p className="success-modal-redirect-text">
                Redirecting you back...
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessModal;